import type {
  StockSyncBreakdown,
  StockSyncDemand,
  StockSyncPreview,
  StockSyncPreviewRow,
} from "@/api/stock_sync";

/** Коды пропуска прогона (`constants.SKIP_*` бэкенда) — словами. */
export const SKIP_TITLE: Record<string, string> = {
  unknown_sku: "товар не найден на маркетплейсе",
  ambiguous: "одному товару 1С соответствует несколько товаров маркетплейса",
  over_delta: "изменение больше допустимого за прогон",
  over_limit: "больше максимума, который принимает маркетплейс",
};

export function skipTitle(code: string): string {
  // Неизвестный код показываем как есть: портал отстал от бэкенда — пусть это будет видно.
  return SKIP_TITLE[code] ?? code;
}

/** Единица — однозначно сопоставленная цель склада (её прогон публикует или держит нулём). */
function isTarget(row: StockSyncPreviewRow): boolean {
  return row.status === "publish" || row.status === "zero_unselected";
}

/**
 * Можно ли менять отметку единицы. Бэкенд (`replace_selection`) принимает в выбор
 * однозначно сопоставленные цели склада и — в любом статусе — единицы, УЖЕ
 * отмеченные на этом складе (стали неоднозначными, пропали, перестали опознаваться):
 * их отметка сохраняется, пока её не снимут. Новую неопознанную, неоднозначную или
 * пропавшую бэкенд отобьёт 422 — галку у них не даём.
 */
export function isSelectable(row: StockSyncPreviewRow): boolean {
  return row.selected || isTarget(row);
}

/** Подсказка у неактивной галки. */
export function lockedHint(row: StockSyncPreviewRow): string | null {
  if (isSelectable(row)) return null;
  switch (row.status) {
    case "zero_unidentified":
      return "товар не найден в 1С по штрихкоду";
    case "ambiguous":
      return "сопоставлена с несколькими товарами 1С — отметить нельзя";
    case "missing":
      return "на складе маркетплейса её больше нет — отметить нельзя";
    default:
      return null;
  }
}

/** Подпись у отмеченной единицы, которую прогон сейчас не публикует (отметка сохранена). */
export function keptHint(row: StockSyncPreviewRow): string | null {
  return row.selected && !isTarget(row)
    ? `отмечена, но сейчас не публикуется: ${row.status_text}`
    : null;
}

export type PreviewFilter = "all" | "selected" | "unselected" | "unmapped" | "problem";

export const FILTERS: { key: PreviewFilter; title: string }[] = [
  { key: "all", title: "Все" },
  { key: "selected", title: "Отмеченные" },
  { key: "unselected", title: "Неотмеченные" },
  { key: "unmapped", title: "Не сопоставленные" },
  { key: "problem", title: "Требуют внимания" },
];

/** Отмечена ли строка сейчас: у отмечаемых — по черновику, у прочих — как в базе (нет). */
export function isChecked(row: StockSyncPreviewRow, selected: ReadonlySet<string>): boolean {
  return isSelectable(row) ? selected.has(row.unit_key) : row.selected;
}

/**
 * Проблемная строка: неоднозначная, пропавшая, придержанная потолком или
 * иначе не публикуемая прогоном, либо маркетплейс не прочитан
 * (`platform_error` — тогда «на маркетплейсе» у строки неизвестно).
 */
export function isProblem(row: StockSyncPreviewRow, preview: StockSyncPreview): boolean {
  if (row.status === "ambiguous" || row.status === "missing") return true;
  if (row.decision_skip || row.max_delta_hold) return true;
  return Boolean(preview.platform_error) && row.will_publish !== null;
}

export function matchesFilter(
  row: StockSyncPreviewRow,
  filter: PreviewFilter,
  selected: ReadonlySet<string>,
  preview: StockSyncPreview,
): boolean {
  switch (filter) {
    case "all":
      return true;
    case "selected":
      return isChecked(row, selected);
    case "unselected":
      return !isChecked(row, selected);
    case "unmapped":
      return row.status === "zero_unidentified" || row.status === "ambiguous";
    case "problem":
      return isProblem(row, preview);
  }
}

export function matchesSearch(row: StockSyncPreviewRow, needle: string): boolean {
  if (!needle) return true;
  return (
    row.article.toLowerCase().includes(needle) ||
    row.unit_key.toLowerCase().includes(needle) ||
    (row.name_1c ?? "").toLowerCase().includes(needle)
  );
}

/** Остаток 1С бывает дробным (весовой товар) — без лишних нулей. */
export function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(3)));
}

/** Спрос юрлица кабинета и владельца вместе (у владельца ключ один). */
export function demandSum(b: StockSyncBreakdown): StockSyncDemand {
  const sum: StockSyncDemand = { in_flight: 0, wb: 0, ozon: 0, total: 0 };
  for (const d of Object.values(b.demand_by_org)) {
    sum.in_flight += d.in_flight;
    sum.wb += d.wb;
    sum.ozon += d.ozon;
    sum.total += d.total;
  }
  return sum;
}

function demandWords(d: StockSyncDemand | undefined): string {
  if (!d) return "0";
  return (
    `${d.total} (собрано, не проведено ${d.in_flight}; заказы Wildberries ${d.wb}; ` +
    `заказы Ozon ${d.ozon})`
  );
}

/**
 * Формула словами — те же слагаемые, что у `PoolLine.explain` бэкенда.
 *
 * Пул: R = O − D_владельца − переток продавцов. Кабинету владельца — max(0, R);
 * кабинету продавца — max(0, S − min(S, D_продавца) + R).
 */
export function explainLines(b: StockSyncBreakdown): string[] {
  const ownerDemand = b.demand_by_org[b.owner]?.total ?? 0;
  const pool =
    `остаток владельца ${fmt(b.owner_stock)} − заказы владельца ${ownerDemand} − ` +
    `заказы продавцов сверх их остатка ${fmt(b.spill_total)} = общий остаток ${fmt(b.pool_rest)}`;
  const formula = b.is_owner
    ? `${pool} → ${b.result}`
    : `${pool}; + остаток продавца ${fmt(b.seller_stock ?? 0)} − покрыто им ` +
      `${fmt(b.seller_use ?? 0)} → ${b.result}`;
  const lines = [formula];
  const raw = b.is_owner
    ? b.pool_rest
    : (b.seller_stock ?? 0) - (b.seller_use ?? 0) + b.pool_rest;
  if (raw < 0) lines.push(`меньше нуля (${fmt(raw)}) — публикуется 0`);
  lines.push(`заказы владельца: ${demandWords(b.demand_by_org[b.owner])}`);
  if (!b.is_owner) {
    lines.push(`заказы юрлица кабинета: ${demandWords(b.demand_by_org[b.org])}`);
    lines.push(
      "«покрыто им» — заказы юрлица кабинета, которые закрываются его собственным " +
        "остатком; заказы продавцов сверх их остатка отгружаются с остатка владельца",
    );
  }
  return lines;
}

/** Изменение больше допустимого: прогон опубликует число, только если
 *  следующий расчёт даст то же самое. */
export function isHeld(row: StockSyncPreviewRow): boolean {
  return row.decision_skip === "over_delta";
}

/** Сейчас на маркетплейсе не то, что будет опубликовано. */
export function isDiverged(row: StockSyncPreviewRow): boolean {
  return (
    row.platform_qty !== null && row.will_publish !== null && row.platform_qty !== row.will_publish
  );
}

export function sameSet(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

export function dateTime(iso: string | null): string {
  return iso ? mskDateTime(iso) : "—";
}

const MSK = new Intl.DateTimeFormat("ru-RU", {
  timeZone: "Europe/Moscow",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/** Дата и время по Москве с пометкой «МСК» — склад и 1С живут по МСК, браузер — где угодно. */
export function mskDateTime(iso: string): string {
  return `${MSK.format(new Date(iso))} МСК`;
}
